import { DialogComponent } from "@components/Dialog";
import MySwitchField from "@components/form/switch";
import { RHFAutocomplete, RHFTextField } from "@components/hook-form";
import { Option } from "@components/hook-form/RHFAutocomplete";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import RHFCountrySelect from "src/components/hook-form/RHFCountrySelect";
import { currencySymbols } from "src/utils/currency";
import type { IFormEngine, ISectionField } from "../../core/types";
import { formatPeppolOptions } from "@pages/ManualDocumentV2/utils/peppolOptions";

interface IEditCustomerInformationProps {
  open: boolean;
  onClose: () => void;
  translate: (text: any, options?: any) => string;
  engine: IFormEngine;
  hiddenFields?: string[];
  extraFields?: ISectionField[];
  customerCompanyIDOptions?: Option[];
}

const ADDRESS_FIELDS = [
  "customerCompanyStreet",
  "customerCompanyStreetNumber",
  "customerCompanyLocality",
  "customerCompanyZipCode",
  "customerCompanyRegion",
];

export const EditCustomerInformation = ({
  open,
  onClose,
  translate,
  engine,
  hiddenFields,
  extraFields,
  customerCompanyIDOptions = [],
}: IEditCustomerInformationProps) => {
  const { getValues, setValue, watch } = useFormContext<any>();
  const isAccountPayable = engine.config.accountClass === "AccountPayable";

  const isFrance = engine.config.complianceStandardKey === "FrancePA";

  const hidden = useMemo(() => new Set(hiddenFields ?? []), [hiddenFields]);
  const isVisible = (key: string) => !hidden.has(key);
  const showAddressSection = ADDRESS_FIELDS.some(isVisible);
  const [isShowLogoURL, setIsShowLogoURL] = useState(false);
  const customerPeppolOptionsFormatted: Option[] = useMemo(() => {
    return formatPeppolOptions(engine.customerPeppolOptions);
  }, [engine.customerPeppolOptions]);

  useEffect(() => {
    if (!open || !customerPeppolOptionsFormatted.length) return;

    const currentParticipant = getValues("customerPeppolParticipantId");
    const currentSelector = getValues("_customerPeppolSelector");
    const currentId =
      typeof currentSelector === "object"
        ? currentSelector?.value
        : currentSelector;

    if (currentParticipant && currentId !== currentParticipant) {
      const match = customerPeppolOptionsFormatted.find(
        (opt) => opt.value === currentParticipant,
      );
      if (match) setValue("_customerPeppolSelector", match);
    }
  }, [open, customerPeppolOptionsFormatted, getValues, setValue]);

  const selectedCustomerPeppolObj = watch("_customerPeppolSelector");

  useEffect(() => {
    if (selectedCustomerPeppolObj === undefined) return;

    const selectedId =
      typeof selectedCustomerPeppolObj === "object"
        ? selectedCustomerPeppolObj?.value
        : selectedCustomerPeppolObj;

    const option = selectedId
      ? engine.customerPeppolOptions?.find(
          (opt: any) => opt.peppolParticipantId === selectedId,
        )
      : null;

    setValue("customerPeppolSchemeId", option?.schemeId || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("customerPeppolParticipantId", option?.peppolParticipantId || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [selectedCustomerPeppolObj, engine.customerPeppolOptions, setValue]);

  useEffect(() => {
    if (getValues("logoURL")) setIsShowLogoURL(true);
  }, []);

  return (
    <DialogComponent
      isShowModal={open}
      showModalMethod={onClose}
      fullWidth
      maxWidth="md"
      contentSx={{ p: 0 }}
      dialogTitle={translate("edit_customer_information")}
      dialogSubTitle={translate("fill_customer_details")}
      dialogContent={
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RHFTextField
                name="customerCompanyName"
                label={translate("customer_company_name")}
                fullWidth
              />
            </Grid>
            {isVisible("customerCompanyID") && (
              <Grid item xs={12} md={6}>
                {customerCompanyIDOptions.length > 1 ? (
                  <RHFAutocomplete
                    options={customerCompanyIDOptions}
                    name="customerCompanyID"
                    label={translate("customer_company_ID")}
                    fullWidth
                    defaultValue={null}
                    freeSolo
                    autoSelect
                  />
                ) : (
                  <RHFTextField
                    name="customerCompanyID"
                    label={translate("customer_company_ID")}
                    fullWidth
                  />
                )}
              </Grid>
            )}
            {isVisible("customerEmail") && (
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="customerEmail"
                  label={translate("customer_email_address")}
                  fullWidth
                />
              </Grid>
            )}
            {isVisible("customerCompanyAddress") && (
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="customerCompanyAddress"
                  label={translate("companyAddress")}
                  fullWidth
                />
              </Grid>
            )}
            {isVisible("customerCountry") && (
              <Grid item xs={12} sm={6}>
                <RHFCountrySelect
                  name="customerCountry"
                  label={translate("country")}
                />
              </Grid>
            )}
            {isVisible("currency") && (
              <Grid item xs={12} sm={6}>
                <RHFAutocomplete
                  name="currency"
                  label={translate("currency")}
                  options={Object.entries(currencySymbols)
                    ?.filter(([key]) => !!key)
                    .map(([key, value]) => ({
                      value: key,
                      label: `${key} ${value}`,
                    }))}
                />
              </Grid>
            )}

            {isAccountPayable && (
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <MySwitchField
                  checked={isShowLogoURL}
                  label={
                    <Typography variant="body2">
                      {translate("add_logo_url")}
                    </Typography>
                  }
                  onChange={() => setIsShowLogoURL((v) => !v)}
                  labelPlacement="end"
                  sx={{ justifyContent: "space-between", alignItems: "center" }}
                />
              </Grid>
            )}
            {isAccountPayable && isShowLogoURL && (
              <Grid item xs={12} sm={6}>
                <RHFTextField
                  name="logoURL"
                  label={translate("logo_url")}
                  fullWidth
                />
              </Grid>
            )}

            {extraFields?.map((field) => {
              const node = field.render({ translate, engine });
              if (!field.grid) {
                return <Fragment key={field.id}>{node}</Fragment>;
              }
              return (
                <Grid
                  key={field.id}
                  item
                  xs={field.grid.xs ?? 12}
                  sm={field.grid.sm}
                  md={field.grid.md}
                  lg={field.grid.lg}
                  xl={field.grid.xl}
                >
                  {node}
                </Grid>
              );
            })}

            {showAddressSection && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    {translate("customer_address")}
                  </Typography>
                </Grid>

                {isVisible("customerCompanyStreet") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="customerCompanyStreet"
                      label={translate("company_street")}
                      fullWidth
                    />
                  </Grid>
                )}
                {isVisible("customerCompanyStreetNumber") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="customerCompanyStreetNumber"
                      label={translate("company_street_number")}
                      fullWidth
                    />
                  </Grid>
                )}
                {isVisible("customerCompanyLocality") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="customerCompanyLocality"
                      label={translate("company_locality")}
                      fullWidth
                    />
                  </Grid>
                )}
                {isVisible("customerCompanyZipCode") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="customerCompanyZipCode"
                      label={translate("company_zip_code")}
                      fullWidth
                    />
                  </Grid>
                )}
                {isVisible("customerCompanyRegion") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="customerCompanyRegion"
                      label={translate("company_region")}
                      fullWidth
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Customer Inbox for France */}
            {isFrance && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    {translate("customer_inbox")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <RHFAutocomplete
                    name="_customerPeppolSelector"
                    label={translate("select_peppol_configuration")}
                    fullWidth
                    options={customerPeppolOptionsFormatted}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      }
      dialogActions={
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="contained" onClick={onClose} size="small">
            {translate("update")}
          </Button>
        </Box>
      }
    />
  );
};
